import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Building2, Search, ArrowUp, ArrowDown } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import BuilderFormSlider from '../components/BuilderFormSlider';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface BuilderAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface Builder {
  _id: string;
  workspaceId: string;
  name: string;
  description?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: {
    number: string;
    formatted: string;
  };
  contactWebsite?: string;
  address: BuilderAddress;
  logoId?: string;
  logoUrl?: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const Builders: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [builders, setBuilders] = useState<Builder[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy] = useState<'name'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Get drawer state from URL params
  const isDrawerOpen = searchParams.get('action') === 'new' || !!searchParams.get('edit');
  const editBuilderId = searchParams.get('edit');

  useEffect(() => {
    if (workspaceId) {
      // Initial load or search query change - full loading
      if (builders.length === 0 || searchQuery !== '') {
        fetchBuilders(false);
      } else {
        // Data refresh (sort, pagination) - only tbody loading
        fetchBuilders(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, pagination.page, searchQuery, sortBy, sortOrder]);

  const fetchBuilders = async (isDataRefresh: boolean = false) => {
    try {
      // Use dataLoading for data refreshes (sort, pagination), loading for initial load
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = await api.get(`/workspaces/${workspaceId}/builders`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          sortBy: sortBy,
          sortOrder: sortOrder
        }
      });

      setBuilders(response.data.builders);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch builders');
      console.error('Error fetching builders:', err);
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleCreateBuilder = () => {
    setSearchParams({ action: 'new' });
  };

  const handleBuilderClick = (builder: Builder) => {
    navigate(`/workspaces/${workspaceId}/builders/${builder._id}`);
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
  };

  const handleFormSuccess = () => {
    setSearchParams({});
    fetchBuilders();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const getSortIcon = () => {
    return sortOrder === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />;
  };

  const getBuilderInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="builders-container">
        {/* Section Header */}
        <div className="builders-section-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 className="builders-section-title">Builders</h2>
              <p className="builders-section-subtitle">
                Manage builder/developer information
              </p>
            </div>
            <button className="btn btn-primary btn-md" onClick={handleCreateBuilder}>
              + New Builder
            </button>
          </div>
        </div>

        {/* Card Container for Search and Table */}
        <div className="builders-card-container">
          {/* Actions Bar */}
          <div className="builders-actions">
            <div className="builders-search">
              <input
                type="text"
                placeholder="Search builders..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
            <div className="builders-filters">
              {/* Future: Add filter and sort buttons */}
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="form-error" style={{ marginBottom: '24px' }}>
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          )}

          {/* Empty State */}
          {!loading && builders.length === 0 && !searchQuery && (
            <div className="builders-empty">
              <div className="builders-empty-icon" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Building2 size={48} style={{ color: '#6B7280' }} />
              </div>
              <h3 className="builders-empty-title">No builders yet</h3>
              <p className="builders-empty-text">
                Get started by adding your first builder
              </p>
              <button className="btn btn-primary btn-lg" onClick={handleCreateBuilder}>
                Create your first builder
              </button>
            </div>
          )}

          {/* No Search Results */}
          {!loading && builders.length === 0 && searchQuery && (
            <div className="builders-empty">
              <div className="builders-empty-icon" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Search size={48} style={{ color: '#6B7280' }} />
              </div>
              <h3 className="builders-empty-title">No builders found</h3>
              <p className="builders-empty-text">
                Try adjusting your search criteria
              </p>
            </div>
          )}

          {/* Builders Table */}
          {!loading && builders.length > 0 && (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Logo</th>
                    <th>
                      <button
                        onClick={handleSort}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Builder Name
                        <span className="sort-icon">
                          {getSortIcon()}
                        </span>
                      </button>
                    </th>
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody className={dataLoading ? 'loading' : ''}>
                  {builders.map((builder, index) => (
                    <tr
                      key={builder._id}
                      onClick={() => handleBuilderClick(builder)}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'
                      }}
                    >
                      <td>
                        {builder.logoUrl ? (
                          <div style={{
                            width: '48px',
                            height: '48px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <img
                              src={builder.logoUrl}
                              alt={builder.name}
                              style={{
                                maxWidth: '48px',
                                maxHeight: '48px',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        ) : (
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#F3F4F6',
                            border: '1px solid #E5E7EB'
                          }}>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#6B7280',
                              textTransform: 'uppercase'
                            }}>
                              {getBuilderInitials(builder.name)}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="data-table-primary-cell">{builder.name}</td>
                      <td>{builder.contactEmail || '—'}</td>
                      <td>{builder.contactPhone?.formatted || '—'}</td>
                      <td>
                        {builder.address
                          ? `${builder.address.city}, ${builder.address.state}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="builders-pagination">
                <button
                  className="builders-pagination-btn"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  ← Prev
                </button>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`builders-pagination-page ${
                      page === pagination.page ? 'active' : ''
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="builders-pagination-btn"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
          )}
        </div>
      </div>

      {/* Builder Form Drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        title={editBuilderId ? 'Edit Builder' : 'Create New Builder'}
        footer={
          <div className="drawer-footer-actions">
            <button
              type="button"
              className="btn btn-secondary btn-md"
              onClick={handleCloseDrawer}
              form="builder-form"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md"
              form="builder-form"
            >
              {editBuilderId ? 'Update Builder' : 'Create Builder'}
            </button>
          </div>
        }
      >
        {workspaceId && (
          <BuilderFormSlider
            workspaceId={workspaceId}
            builderId={editBuilderId || undefined}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseDrawer}
          />
        )}
      </Drawer>
    </AppLayout>
  );
};

export default Builders;
